import { format } from "date-fns";
import {
  IDropdown,
  QueryParamType,
  ISelectOption,
  IListOfValues,
  ISelectOptionNonGroup,
  ILocationData,
  IRange,
  IListOfValue,
  IHashMap,
  DebouncedFunction,
  TimeoutId,
  ITabItem,
  IStateData,
  ILocation
} from "../interface/types";
import { DateTimeFormat, DateFormat } from "../constant";
import { isValidPhoneNumber } from "react-phone-number-input";
import { openNotification } from "components/shared/errorNotification/errorNotification";
import { urlFileNameExtractorRegex } from "shared/regex";
import { IAssembly } from "components/management/auto/assembly/assembly.interface";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const regex = /^[0-9]/;
const minimumNtnNumber = 7;
const maximumNtnNumber = 8;

export const isPasswordMatch = (value: string, newPasswordValue: string): boolean => {
  if (!value || newPasswordValue === value) {
    return true;
  }
  return false;
};

export const validator = ({ getFieldValue }: { getFieldValue: Function }, t: Function): { validator(_: object, value: string): Promise<void> } => ({
  validator(_: object, value: string): Promise<void> {
    if (isPasswordMatch(value, getFieldValue("password"))) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(t("set_password_re_enter_password_validation_text")));
  }
});

export const passwordValidator = (
  { getFieldValue }: { getFieldValue: Function },
  t: Function
): { validator(_: object, value: string): Promise<void> } => ({
  validator(_: object, value: string): Promise<void> {
    if (isPasswordMatch(value, getFieldValue("newPassword"))) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(t("set_password_re_enter_password_validation_text")));
  }
});

export const formatTime = (time: number): string => {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  const formattedTimer = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return formattedTimer;
};
export const capitalizeCase = (str: string): string => {
  return str[0].toUpperCase() + str.slice(1);
};

export const dropDownPayload = (
  payload: IDropdown[] | IListOfValues[],
  valueKey: keyof (IListOfValues | IDropdown) = "id",
  extraKey?: keyof (IListOfValues | IDropdown)
): ISelectOptionNonGroup[] | [] => {
  if (payload?.length) {
    return payload?.map((item: IDropdown | IListOfValues) => ({
      label: item.value,
      // this key is by default id but if author want any other value to be the value of `value key` now author can
      value: item[valueKey],
      // one can add extra key as per payload OR business requirements
      ...(extraKey && { extraKey: item[extraKey] })
    }));
  } else {
    return [];
  }
};

export const getDynamicdropdownPayload = ({
  payload,
  valueKey = "id",
  labelKey = "value",
  extraKey
}: {
  payload: IDropdown[] | IListOfValues[] | ISelectOptionNonGroup[];
  valueKey?: string;
  labelKey?: string;
  extraKey?: keyof (IListOfValues | IDropdown | ISelectOptionNonGroup);
}): ISelectOptionNonGroup[] | [] => {
  if (payload?.length) {
    return payload?.map((item: IDropdown | IListOfValues | ISelectOptionNonGroup) => ({
      label: item[labelKey as keyof (IListOfValues | IDropdown | ISelectOptionNonGroup)] as string,
      // this key is by default id but if author want any other value to be the value of `value key` now author can
      value: item[valueKey as keyof (IListOfValues | IDropdown | ISelectOptionNonGroup)],
      // one can add extra key as per payload OR business requirements
      ...(extraKey && { extraKey: item[extraKey] })
    }));
  } else {
    return [];
  }
};

export const dropDownPayloadLocation = (payload: ILocationData[]): ISelectOptionNonGroup[] | [] => {
  if (payload?.length) {
    return payload?.map((location: ILocationData) => ({
      value: location.cityId.toString(),
      label: `${capitalizeCase(location.cityName)}, ${capitalizeCase(location.stateName)}, ${capitalizeCase(location.countryName)}`
    }));
  } else {
    return [];
  }
};

export const getAPIUrl = (endPoint: string, queryParams?: QueryParamType): string => {
  if (!endPoint) return "";
  if (!queryParams) return endPoint;

  let queryString = "";
  for (const key in queryParams) {
    const value = queryParams[key];
    if (value) {
      queryString += `${queryString ? "&" : ""}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    }
  }

  const apiUrl = `${endPoint}?${queryString}`;
  return apiUrl;
};

export const appendAdditionalOption = (
  payload: ISelectOption[] | [],
  option: ISelectOption,
  position: "start" | "end" = "start"
): ISelectOption[] => {
  if (!payload?.length) {
    return [];
  }

  const isStart = position === "start";
  if (isStart) return [option, ...payload];
  else return [...payload, option];
};

export const getLastUpdate = (updatedAt: Date, name: string): string => {
  const formattedDate = format(new Date(updatedAt), DateTimeFormat);
  const displayText = `${formattedDate} - ${name}`;

  return displayText;
};
export const getDateOnly = (date: Date): string => {
  if (!date) return "";
  const formattedDate = format(new Date(date), DateFormat);
  const displayText = `${formattedDate}`;
  return displayText;
};
/**
 * @function phoneNumberValidator validate phone number for all countries
 * ( this is the default way to write validation function for ant design form )
 * we are using here the builtin function of react-phone-number-input package
 * @param t for translation
 * @returns Promise: will be resolved if it work fine and reject if number is invalid
 */

export const phoneNumberValidator = (t: Function): { validator(_: object, value: string): Promise<void> } => ({
  validator(_e: object, value: string): Promise<void> {
    if (value && !isValidPhoneNumber(value)) {
      return Promise.reject(new Error(t("phone_number_error_invalid")));
    }
    return Promise.resolve();
  }
});

export const getAllMembers = (value: { value: string }[], allowedCount?: number): string => {
  if (!value?.length) return "";
  const sortedValue = [...value].sort((a, b) => {
    if (a.value < b.value) return 1;
    if (a.value > b.value) return -1;
    return 0;
  });

  return `${sortedValue?.reduce(
    (updatedMembers: string, newMember: { value: string; allowedCount?: number }) =>
      `${updatedMembers && `${updatedMembers} + `}${newMember.value}${newMember?.allowedCount ? `(${newMember?.allowedCount})` : ""}`,
    ""
  )}${allowedCount ? ` ${allowedCount}` : ""}`;
};

export const convertYearToDays = (year: number): number => {
  return year * 365;
};

export const ntnValidator = (t: Function): { validator(_: object, value: number): Promise<void> } => ({
  validator(_: object, value: number): Promise<void> {
    if (value && !regex.test(value.toString())) {
      return Promise.reject(new Error(t("ntn_no_validation")));
    } else if (value && Math.abs(value).toString().length < minimumNtnNumber) {
      return Promise.reject(new Error(t("ntn_no_min_limit")));
    } else if (value && Math.abs(value).toString().length > maximumNtnNumber) {
      return Promise.reject(new Error(t("ntn_no_max_limit")));
    } else if (value && value < 0) {
      return Promise.reject(new Error(t("ntn_no_validation")));
    }
    return Promise.resolve();
  }
});

/**
 * Adds commas to a number to format it with thousands separators.
 *
 * @param {number} number - The number to format.
 * @returns {string} The formatted number as a string with commas.
 */
export const addCommasToNumber = (number: number): string => {
  const numberString: string = number.toString();

  const parts: string[] = numberString.split(".");

  const integerPart: string = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const result: string = parts.length === 2 ? `${integerPart}.${parts[1]}` : integerPart;

  return result;
};

export const formatNumberInPKStandard = (number: number): string => {
  const numberString: string = number.toString();

  const parts: string[] = numberString.split(".");

  const integerPart: string = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const result: string = parts.length === 2 ? `${integerPart}.${parts[1]}` : integerPart;
  return result;
};
export const checkIfRangeExist = (range: IRange, existingRanges: IRange[]): boolean => {
  if (!existingRanges || !range) return false;
  const { min, max } = range;
  for (const existingRange of existingRanges) {
    const { min: existingRangeMin, max: existingRangeMax } = existingRange;

    const isMinInRange = min > existingRangeMin && min < existingRangeMax;
    const isMaxInRange = max > existingRangeMin && min < existingRangeMax;

    if (isMinInRange || isMaxInRange) return true;
  }
  return false;
};

export const getCountryWithAreaCode = (phoneNumber: string): string => {
  const countryCode = phoneNumber.slice(1, 3);
  const areaCode = phoneNumber.slice(3, 6);
  return `${countryCode} ${areaCode}`;
};

/**
 * Creates a hash map from an array of objects.
 * @param arrayOfObjects - The array of objects to create the hash map from.
 * @returns The created hash map.
 */
export const createHashMap = (arrayOfObjects: IListOfValue[]): IHashMap => {
  // Initialize an empty hash map
  const hashMap: IHashMap = {};

  // Loop through the array and populate the hash map
  for (const obj of arrayOfObjects) {
    hashMap[obj.value] = obj.id;
  }

  return hashMap;
};

export function customDebounce<F extends (...args: never[]) => void>(func: F, delay: number): DebouncedFunction<F> {
  let timeoutId: TimeoutId | null = null;
  return function (...args: Parameters<F>) {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  };
}

/**
 * Returns an object from an array based on the specified key-value pair.
 *
 * @param {Object[]} arr - The array to search in.
 * @param {string} key - The key to match.
 * @param {number | string} value - The value to match.
 * @return {any} The object that matches the key-value pair, or an empty object if no match is found.
 */
export const getObjectFromArrayById = (arr: {}[], key: string, value: number | string): {} => {
  return arr.find((x: { [key: string]: number | string }) => x[key] == value) || {};
};

export const getArrayToHashMap = (payload: ISelectOptionNonGroup[]): IHashMap => {
  const hashMap: IHashMap = {};
  payload.forEach(({ value, label }: ISelectOptionNonGroup) => {
    hashMap[value as number] = label;
  });
  return hashMap;
};

export const getArrayToHashMapByName = (payload: ISelectOptionNonGroup[]): IHashMap => {
  const hashMap: IHashMap = {};
  payload.forEach(({ value, label }: ISelectOptionNonGroup) => {
    hashMap[label as string] = value as number;
  });
  return hashMap;
};

export const getPermittedTabs = (
  tabs: ITabItem[],
  ActiveFeature: { [x: string]: number | boolean },
  AllowedPermissions: { [x: string]: {} }
): ITabItem[] => {
  const PermittedTab: ITabItem[] = [];
  tabs.map((data: ITabItem) => {
    const isActiveFeature = ActiveFeature[data.featureKey as keyof typeof ActiveFeature];
    const isAllowedRoute = AllowedPermissions[data.permissionGroupKey as keyof typeof AllowedPermissions];
    if (((!!isActiveFeature && !!isAllowedRoute) || (!data.featureKey && !data.permissionGroupKey)) && !data.isHide) {
      PermittedTab.push(data);
    }
  });
  return PermittedTab;
};

export const developmentInProgressNotificationOpener = (): void => {
  openNotification("info", "Development under progress");
};

export const isNonZeroFalse = (value: string | number): boolean => {
  return !value && typeof value !== "number";
};

export const truncateString = (str: string, length: number): string => {
  const dots = str.length > length ? "..." : "";
  return str.substring(0, length) + dots;
};

export const extractFileNameFromURL = (url: string): string => {
  const match = url?.match(urlFileNameExtractorRegex);
  const fileNameUUID = match ? match[1] : "";
  const parts = fileNameUUID.split("-");
  return parts[parts.length - 1];
};

export const extractKeyFromUrl = (url: string): string => {
  const match = url?.match(/\/([^/]+)$/);
  return match ? match[1] : "";
};
export const NumberToStringCounts = {
  1: "First",
  2: "Second",
  3: "Third",
  4: "Fourth"
};

export const createQueryString = (queryObject: object): string => {
  const queryString = Object.keys(queryObject)
    .map(key => `${key}=${encodeURIComponent(queryObject[key as keyof typeof queryObject])}`)
    .join("&");
  if (queryString) {
    return `?${queryString}`;
  }
  return "";
};

export const downloadFile = (file: string, fileName: string): void => {
  const url = window.URL.createObjectURL(new Blob([file]));

  // Create a link element
  const link = document.createElement("a");
  link.href = url;

  // Set the file name and download
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();

  // Clean up
  link.parentNode?.removeChild(link);
};

export const DropDownPayloadState = (payload: IStateData[] | null): ISelectOption[] => {
  if (payload?.length) {
    return payload?.map(item => ({
      value: item.id,
      label: `${capitalizeCase(item.stateName)}, ${capitalizeCase(item?.countryName)}`
    }));
  } else {
    return [];
  }
};

export const DropDownPayloadAssembly = (payload: IAssembly[] | null): ISelectOption[] => {
  if (payload?.length) {
    return payload?.map(item => ({
      value: item.id,
      label: item.assembly
    }));
  } else {
    return [];
  }
};

export const convertedArray = (mixedArray: (number | string | { id: number })[], oldLocation: []): number[] => {
  let ids = [];
  const array: number[] = mixedArray.map((item: string | number | { id: number }) => {
    if (typeof item === "string") {
      return parseInt(item, 10);
    } else {
      return item as number;
    }
  });
  ids = oldLocation.map((old: { id: number }) => old.id);
  const integersOnly = array.concat(ids).filter(item => Number.isInteger(item));
  return integersOnly;
};

export function customDelay<T>(callback: () => Promise<T>, timer = 1500): Promise<T> {
  return new Promise(resolve => {
    setTimeout(async () => {
      const result = await callback();
      resolve(result);
    }, timer);
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function exportToExcel<T>(data: T[], fileName: string) {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const file = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(file, fileName);
}

export const convertToTitleCase = (inputString: string): string => {
  return inputString.replace(/([A-Z])/g, " $1").replace(/^./, firstChar => firstChar.toUpperCase());
};

export const dropDownPayloadWithCustomDisable = (
  payload: IDropdown[] | IListOfValues[],
  selectedSurveyType: string,
  valueKey: keyof (IListOfValues | IDropdown) = "id"
): ISelectOptionNonGroup[] | [] => {
  if (payload?.length) {
    return payload?.map((item: IDropdown | IListOfValues) => ({
      label: item.value,
      // this key is by default id but if author want any other value to be the value of `value key` now author can
      value: item[valueKey],
      disabled: item.key == selectedSurveyType ? false : true
    }));
  } else {
    return [];
  }
};

export const formatCityText = (city: ILocation): string => {
  if (city && Object.keys(city)?.length) {
    return `${city?.value}, ${city?.stateName}, ${city?.countryName}`;
  }
  return "";
};

export const isUndefineOrNegativeValue = (value?: number): boolean => {
  return !value || value < 0;
};
